import classNames from "classnames";
import { useChatStoreActions } from "PFApp/stores/chat_store";
import useIsAuditPlannerPermitted from "PFApp/use_is_audit_planner_permitted";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import useIsWorkflowPermitted from "PFApp/use_is_workflow_permitted";
import { useProfilePreviewData } from "PFApp/use_profile_preview";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import { isMsTeams, openChatInMsTeams } from "PFCore/helpers/ms_teams";
import { isProfileActive } from "PFCore/helpers/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { fetchConversations } from "PFCore/services/chat/fetch_conversations";
import { saveConversation } from "PFCore/services/chat/save_conversation";
import { GId } from "PFCore/types";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import css from "./profile_preview_action_buttons.module.scss";

const ProfilePreviewActionButtons = () => {
  const {
    data: { profile, id }
  } = useProfilePreviewData();
  const { data: currentProfile } = useCurrentProfile();

  const { renderChat } = useChatStoreActions();

  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation("profiles", { keyPrefix: "preview" });

  const isWorkflowPermitted = useIsWorkflowPermitted();
  const isAuditPlannerPermitted = useIsAuditPlannerPermitted();

  const isChatEnabled = useIsChatEnabled();

  const isActive = isProfileActive(profile);
  const canChat = isChatEnabled && profile && isActive && currentProfile.id !== profile.id;

  const onMatchingRolesClick = () => {
    // @ts-ignore
    const utmSource = location?.state?.utmSource;
    navigate({ pathname: `/profiles/${id}/matching_roles` }, { state: { utmSource } });
  };

  const handleChatClick = (event) => {
    if (!profile) {
      return;
    }
    event.preventDefault();
    if (isMsTeams()) {
      return openChatInMsTeams(profile.email);
    }
    const uniqueProfileTargetId = Math.min(profile.id, currentProfile.id);
    const target: GId = `gid://profinda-api/Profile/${uniqueProfileTargetId}`;
    const createTarget: GId = `gid://profinda-api/Profile/${profile.id}`;

    fetchConversations({ target, oneToOneProfileId: profile.id }).then(({ entries }) => {
      if (entries && entries.length > 0) {
        renderChat(entries[0].id);
      } else {
        saveConversation({ target: createTarget }).then((response) => {
          renderChat(response.id);
        });
      }
    });
  };

  return (
    <div className={classNames(css.buttons, css.bottomLine)}>
      {profile?.viewable && (
        <ButtonLink qaId="ProfilePreview.viewFull" href={`/profiles/${id}`} key="profile">
          {t("viewFullProfile")}
        </ButtonLink>
      )}
      {isActive && (isWorkflowPermitted || isAuditPlannerPermitted) && (
        <Button kind="secondary" onClick={onMatchingRolesClick} key="match">
          {t("matchingRoles")}
        </Button>
      )}
      {canChat && <Button onClick={handleChatClick} kind="secondary" icon="chat" />}
    </div>
  );
};

export default ProfilePreviewActionButtons;
